// src/utils/notifications.js
const isNotificationDebugEnabled = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem('notification_debug') === 'true';
  } catch {
    return false;
  }
};

const debugNotificationFlow = (event, details = {}) => {
  if (!isNotificationDebugEnabled()) return;
  console.debug('[notification-flow]', event, details);
};

export function isNotificationSupported() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export async function requestNotificationPermission() {
  if (!isNotificationSupported()) return 'denied';
  if (Notification.permission !== 'default') return Notification.permission; // 'granted' ou 'denied'
  // importante: chame essa função após um clique do usuário
  return await Notification.requestPermission();
}

export function shouldEmitSystemNotification({
  pageVisible = false,
  systemWhenVisible = false,
  hasOtherActiveTab = false,
} = {}) {
  if (hasOtherActiveTab) return false;
  return !pageVisible || systemWhenVisible;
}

export function notify(
  title,
  {
    body,
    icon,
    tag,
    requireInteraction = false,
    autoCloseAfterMs = 4000,
    debugMeta,
    onClick,
  } = {}
) {
  if (!isNotificationSupported()) return;
  if (Notification.permission !== 'granted') return;

  let notification;
  const notificationDebugMeta = {
    conversation_id: debugMeta?.conversation_id ?? debugMeta?.conversationId ?? debugMeta?.chat_id ?? debugMeta?.chatId ?? null,
    chat_id: debugMeta?.chat_id ?? debugMeta?.chatId ?? null,
    message_key: debugMeta?.message_key ?? debugMeta?.messageKey ?? null,
    notification_kind: debugMeta?.notification_kind ?? debugMeta?.kind ?? null,
    tag: tag || debugMeta?.tag || null,
    permission: Notification.permission,
  };
  try {
    debugNotificationFlow('system_attempt', notificationDebugMeta);
    // exibe a notificação do sistema quando permitido
    notification = new Notification(title, {
      body: body ?? '',
      icon: icon ?? '/favicon.ico',
      tag,
      requireInteraction: Boolean(requireInteraction),
    });
    debugNotificationFlow('system_created', notificationDebugMeta);
  } catch (err) {
    debugNotificationFlow('system_error', {
      ...notificationDebugMeta,
      error_name: err?.name || null,
      error_message: err?.message || String(err || ''),
    });
    console.warn('O navegador não conseguiu exibir a notificação do sistema.', err);
    return null;
  }

  if (
    typeof autoCloseAfterMs === 'number' &&
    autoCloseAfterMs > 0 &&
    !requireInteraction
  ) {
    const closeTimeout = window.setTimeout(() => notification.close(), autoCloseAfterMs);
    notification.addEventListener('close', () => {
      window.clearTimeout(closeTimeout);
    });
  }

  if (typeof onClick === 'function') {
    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      onClick();
      notification.close();
    };
  }

  return notification;
}

let newClientAudio;
let newMessageAudio;
let newInternalChatMessageAudio;
const NOTIFICATION_VOLUME_KEY = 'notification_volume_v1';
const MESSAGE_SOUND_COOLDOWN_MS = 1500;
const INTERNAL_CHAT_SOUND_COOLDOWN_MS = 1500;

const lastSoundPlayedAt = {
  message: 0,
  internalChat: 0,
};

const clampVolume = (v) => {
  if (Number.isNaN(Number(v))) return 1;
  return Math.min(1, Math.max(0, Number(v)));
};

const shouldPlayThrottledSound = (key, cooldownMs) => {
  const now = Date.now();
  const lastPlayedAt = Number(lastSoundPlayedAt[key] || 0);
  if (lastPlayedAt && now - lastPlayedAt < cooldownMs) {
    return false;
  }
  lastSoundPlayedAt[key] = now;
  return true;
};

export function getNotificationVolume() {
  try {
    const raw = localStorage.getItem(NOTIFICATION_VOLUME_KEY);
    if (raw === null || raw === undefined) return 1;
    return clampVolume(parseFloat(raw));
  } catch {
    return 1;
  }
}

export function setNotificationVolume(volume) {
  const clamped = clampVolume(volume);
  try {
    localStorage.setItem(NOTIFICATION_VOLUME_KEY, clamped.toString());
  } catch {
    // ignore
  }

  if (newClientAudio) newClientAudio.volume = clamped;
  if (newMessageAudio) newMessageAudio.volume = clamped;
  if (newInternalChatMessageAudio) newInternalChatMessageAudio.volume = clamped;
}

export function playNewClientNotificationSound(loop = false) {
  if (typeof window === 'undefined') return;
  try {
    if (!newClientAudio) {
      newClientAudio = new Audio('/new_client.wav');
    }
    newClientAudio.volume = getNotificationVolume();
    newClientAudio.loop = loop;
    newClientAudio.currentTime = 0;
    const playPromise = newClientAudio.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
  } catch (err) {
    // Falha ao reproduzir o áudio (ex.: falta de interação do usuário)
    console.warn('Som de notificação bloqueado até interação do usuário.', err?.message || err);
  }
}

export function stopNewClientNotificationSound() {
  if (!newClientAudio) return;
  try {
    newClientAudio.pause();
    newClientAudio.currentTime = 0;
    newClientAudio.loop = false;
  } catch (err) {
    console.error('Erro ao parar som de notificação:', err);
  }
}

export function playNewMessageNotificationSound() {
  if (typeof window === 'undefined') return;
  if (!shouldPlayThrottledSound('message', MESSAGE_SOUND_COOLDOWN_MS)) return;
  try {
    if (!newMessageAudio) {
      newMessageAudio = new Audio('/new_message.wav');
    }
    newMessageAudio.volume = getNotificationVolume();
    newMessageAudio.currentTime = 0;
    const playPromise = newMessageAudio.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
  } catch (err) {
    // Falha ao reproduzir o áudio (ex.: falta de interação do usuário)
    console.warn('Som de notificação bloqueado até interação do usuário.', err?.message || err);
  }
}

export function playInternalChatMessageNotificationSound() {
  if (typeof window === 'undefined') return;
  if (!shouldPlayThrottledSound('internalChat', INTERNAL_CHAT_SOUND_COOLDOWN_MS)) return;
  try {
    if (!newInternalChatMessageAudio) {
      newInternalChatMessageAudio = new Audio('/new_internal_chat_message.wav');
    }
    newInternalChatMessageAudio.volume = getNotificationVolume();
    newInternalChatMessageAudio.currentTime = 0;
    const playPromise = newInternalChatMessageAudio.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
  } catch (err) {
    console.warn('Som de notificação do chat interno bloqueado até interação do usuário.', err?.message || err);
  }
}
